import { useLayoutEffect, useRef, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";

function Caroussel({ images }) {
    const widthImage = 300;
    const heightImage = 300;
    const gap = 36;
    const scale = 1.2; // how much the active image grows

    const trackRef = useRef(null);
    const viewportRef = useRef(null);

    const middleIndex = Math.floor(images.length / 2);
    const [activeImage, setActiveImage] = useState(middleIndex);
    const [viewportWidth, setViewportWidth] = useState(0);
    const isMobile = viewportWidth <= 544; // align with mobile breakpoint in CSS

    useLayoutEffect(() => {
        const measure = () => {
            if (viewportRef.current)
                setViewportWidth(viewportRef.current.clientWidth);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    const slideWidth = widthImage + gap;
    const totalWidth = images.length * widthImage + (images.length - 1) * gap;
    const edgePadding = ((scale - 1) * widthImage) / 2; // extra width added on each side when scaled
    // For the last slide, nudge a bit more than edgePadding, but not the full gap
    const edgeShiftLastAndFirst = edgePadding + gap / 4;

    const desiredOffset =
        activeImage * slideWidth + widthImage / 2 - viewportWidth / 2;

    const clampedOffset = Math.max(
        0,
        Math.min(desiredOffset, Math.max(0, totalWidth - viewportWidth))
    );
    const edgeShift =
        activeImage === images.length - 1 ? edgeShiftLastAndFirst : 0; // nudge last slide left so the scaled part stays in view

    // Enough vertical padding so the scaled image isn't clipped by the viewport,
    // while still keeping overflow hidden (no scrollbars).
    const verticalPad = ((scale - 1) * heightImage) / 2 + 8;

    return (
        <div
            ref={viewportRef}
            className="caroussel-viewport"
            style={{
                position: "relative",
                width: "100%",
                paddingBlock: `${verticalPad}px 0`, // room for the taller scaled image
                userSelect: "none",
                isolation: "isolate",
            }}
        >
            <button
                onClick={() => setActiveImage((i) => (i !== 0 ? i - 1 : i))}
                className="caroussel-btn caroussel-btn-prev"
            >
                <ion-icon name="arrow-back-circle-outline"></ion-icon>
            </button>
            <div
                ref={trackRef}
                className="caroussel-track"
                style={{
                    display: "flex",
                    gap: `${gap}px`,
                    alignItems: "center",
                    transition: "transform 0.5s ease",
                    transform: `translateX(-${clampedOffset + edgeShift}px)`,
                    willChange: "transform",
                    // keep overflow visible so the scaled slide can draw over neighbors
                    overflow: "visible",
                }}
            >
                {images.map((image, i) => {
                    const isActive = activeImage === i;
                    return (
                        <LazyLoadImage
                            key={i}
                            width={`${widthImage}px`}
                            height={`${heightImage}px`}
                            src={image}
                            effect="blur"
                            onClick={() => setActiveImage(i)}
                            className={`caroussel-image ${
                                isActive ? "active-image" : ""
                            }`}
                            style={{
                                flex: "0 0 auto",
                                objectFit: "cover",
                                borderRadius: "8px",
                                cursor: "pointer",
                                transition: "transform 0.3s ease",
                                transformOrigin: "center center",
                                // visual scaling without changing layout width/height
                                transform: `translateZ(0) scale(${
                                    isActive ? scale : 1
                                })`,
                                // make the active one render above neighbors while scaled
                                position: "relative",
                                zIndex: isActive ? 2 : 1,
                                // smoother GPU hint
                                willChange: "transform",
                                paddingLeft: `${
                                    activeImage === 0 && isMobile
                                        ? `${edgeShiftLastAndFirst}px`
                                        : 0
                                }`,
                            }}
                        />
                    );
                })}
            </div>
            <button
                onClick={() =>
                    setActiveImage((i) => (i !== images.length - 1 ? i + 1 : i))
                }
                className="caroussel-btn caroussel-btn-next"
            >
                <ion-icon name="arrow-forward-circle-outline"></ion-icon>
            </button>
        </div>
    );
}

export default Caroussel;
